import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FIXED_IFRAME_MESSAGE_IDS,
  selectHoveredTempoElement,
  selectMultiSelectedTempoElements,
  selectSelectedTempoElement,
  setAllSelectedElementKeys,
  setHoveredElementKey,
  setRequestedContextPosition,
  setSelectedElementKey,
  setShowContextMenu,
} from "../../store/iframeSlice";
import {
  CursorType,
  ModalType,
  setCursorType,
  setModal,
} from "../../store/globalSettingsSlice";
import { toast } from "sonner";
import {
  useActiveCanvas,
  useActiveMainCanvas,
  useActiveProject,
} from "@/utils/utils";
import { v4 as uuidv4 } from "uuid";
import {
  DEFAULT_MOBILE_SCREEN_SIZE,
  DEFAULT_SCREEN_SIZE,
  EMPTY_TREE_CODEBASE_ID,
  STORYBOARD_TYPES,
} from "@/utils/constants";
import CanvasContextMenu from "./CanvasContextMenu";
import { NavTreeNode, Storyboard } from "@/utils/types";
import { track } from "@/utils/events/tracker";
import _ from "lodash";
import { useUpdateMyPresence } from "../../../liveblocks.config";

// @ts-ignore
import LiveblocksCursors from "../LiveblocksCursors";
import {
  useSendDrawingDiv,
  useThrottledSendSyntheticMouseOverEvent,
  useThrottledSetHoveredElement,
  useThrottledSyntheticMouseMove,
} from "@/utils/iframeUtils";
import {
  MAX_STORYBOARD_HEIGHT,
  MAX_STORYBOARD_WIDTH,
  MIN_STORY_HEIGHT,
  MIN_STORY_WIDTH,
  MIN_STORYBOARD_HEIGHT,
  MIN_STORYBOARD_WIDTH,
  useCreateStoryboard,
} from "@/hooks/storyboards/useCreateStoryboard";
import { TempoElement } from "@/utils/tempoElement";
import {
  AddJsxChange,
  AnyChangeLedgerItem,
} from "@/store/zustand/changeLedgerTypes";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import {
  onCanvasesSnapshot,
  onSubCanvasesSnapshot,
  setIgnoreCanvasUpdates,
  updateCanvasStoryboards,
} from "@/store/supabaseSlice";
import { CanvasOverlay } from "./CanvasOverlay";
import { useCanvasPos, useCanvasStore } from "@/store/zustand/canvasStore";
import { useCopyPaste } from "@/components/Canvas/hooks/useCopyPaste";
import { DependencyConfirmationModalWrapper } from "../DependencyConfirmationModal";
import { FileConflictModalWrapper } from "../FileConflictModal";
import { StoryboardView } from "./Storyboard";
import { useIframePostMessaging } from "./hooks/useIframePostMessaging";
import { getExpectedIndexForPos, useIframeDrag } from "./hooks/useIframeDrag";
import { IframePool } from "./IframePool";
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  DEFAULT_STORYBOARD_TEXT_HEIGHT,
  DEFAULT_STORYBOARD_TEXT_WIDTH,
  DRAG_IN_INITIAL_HEIGHT,
  DRAG_IN_INITIAL_WIDTH,
  DRAG_IN_LARGER_HEIGHT,
  DRAG_IN_LARGER_WIDTH,
  getImageUrlForElement,
  getPointOnCanvasFromInsideIframe,
  getStoryboardForPoint,
  normalizeToIFrameCoordinates,
  StoryboardChangeType,
} from "./utils";
import { useOnMouseMove } from "./hooks/useOnMouseMove";
import {
  useOnKeyDown,
  useOnKeyUp,
  useOnMouseDown,
  useOnMouseUp,
} from "./hooks/useKeyEvents";
import { CanvasMovementOverlay } from "./CanvasMovementOverlay";
import { getStoriesForComponent } from "@/utils/extractElementsUtils";
import { useElementMetadata } from "../contexts/ElementMetadataContext";
import { PlusCircledIcon } from "@radix-ui/react-icons";
import {
  createDummyComponent,
  wrapWithBlankStoryboard,
  wrapWithBlankStoryboardNoTailwind,
} from "../AppHeader/utils";
import {
  useInitializeStoryboard,
  useInitializeStoryStoryboard,
} from "@/hooks/storyboards/useInitializeStoryboards";
import useThrottledState from "@/hooks/useThrottledState";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useAssetComponents } from "@/hooks/useAssetComponents";
import useTypographyPresets from "@/components/AppHeader/hooks/useTypographyPresets";
import useLayoutPresets, {
  LayoutId,
  LayoutPreset,
} from "@/components/AppHeader/hooks/useLayoutPresets";
import Annotations from "./Annoations";
import { useLoadCodebase } from "@/utils/devServerUtils";
import useLayoutStore, { LayoutMode } from "@/store/zustand/layoutStore";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import { useCanvasId } from "@/utils/utils";
import { useSearchParams } from "react-router";
import { ClientSideSuspense } from "@liveblocks/react";

import CanvasLoadingOverlay from "./CanvasLoadingOverlay";
import { ReactFramework } from "@/utils/templateProjects";
import { useReactComponentHandler } from "../../hooks/useReactComponentHandler";
import { LoadingSpinner } from "./LoadingSpinner";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { useIframeStore } from "@/store/zustand/iframeStore";
import { ScalablePanningLayer } from "./ScalablePanningLayer";
import { CanvasSideEffect } from "./CanvasSideEffect";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { enqueueElementsForScreenshotGeneration } from "@/store/metadataSlice";

interface Props {}

const Canvas: React.FC<Props> = React.memo((props) => {
  const activeCanvas = useActiveCanvas();
  const project = useActiveProject();
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  const activeCanvasRef = useRef(activeCanvas);
  useEffect(() => {
    activeCanvasRef.current = activeCanvas;
  }, [activeCanvas]);

  const canvasId = useCanvasId();
  const isLoading = useCanvasStore((state) => state.isLoading);
  const setIsLoading = useCanvasStore((state) => state.setIsLoading);

  const [searchParams, setSearchParams] = useSearchParams();

  const componentId = searchParams.get("componentId");
  const prompt = searchParams.get("prompt");

  const { triggerRefetchProjectLoadingProgress } = useSupabase();

  const setSendChatMessage = useAiContextStore(
    (state) => state.setSendChatMessage
  );

  const dispatch = useDispatch<any>();

  const selectedElement: TempoElement = useSelector(selectSelectedTempoElement);
  const selectedElementRef = useRef(selectedElement);
  useEffect(() => {
    selectedElementRef.current = selectedElement;
  }, [selectedElement]);

  const multiselectedElements: TempoElement[] = useSelector(
    selectMultiSelectedTempoElements
  );
  const multiselectedElementsRef = useRef(multiselectedElements);
  useEffect(() => {
    multiselectedElementsRef.current = multiselectedElements;
  }, [multiselectedElements]);

  const hoveredElement: TempoElement = useSelector(selectHoveredTempoElement);
  const hoveredElementRef = useRef(hoveredElement);
  useEffect(() => {
    hoveredElementRef.current = hoveredElement;
  }, [hoveredElement]);

  const showContextMenu = useSelector(
    (state: any) => state.iframe.showContextMenu
  );

  const { metadata } = useElementMetadata();

  const requestedContextPosition = useSelector(
    (state: any) => state.iframe.requestedContextPosition
  );

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup
  );
  const projectLaunchStatus = useCodebaseStore(
    (state) => state.projectLaunchStatus
  );
  const { componentKeyToComponent } = useAssetComponents();
  const initializeStoryboard = useInitializeStoryboard();
  const initializeStoryStoryboard = useInitializeStoryStoryboard();
  const createStoryboard = useCreateStoryboard();

  const executeCommand = useDesignCommandManager(
    (state) => state.executeCommand
  );

  const draggingComponent = useSelector(
    (state: any) => state.iframe.draggingComponent
  );

  const cursorType = useSelector(
    (state: any) => state.globalSettings.cursorType
  );
  const cursorTypeRef = useRef(cursorType);
  useEffect(() => {
    cursorTypeRef.current = cursorType;
  }, [cursorType]);

  const loadCodebase = useLoadCodebase();

  const gitStatus = useSelector((state: any) => state.gitStatus.gitStatus);

  // For resizing & moving storyboards
  const [changeStoryboardId, setChangeStoryboardId] = useState(null);
  const changeStoryboardIdRef = useRef(changeStoryboardId);
  useEffect(() => {
    changeStoryboardIdRef.current = changeStoryboardId;
  }, [changeStoryboardId]);

  // @ts-expect-error TS2345
  const [changeType, setChangeType] = useState<StoryboardChangeType>(null);
  const [changeStartCoords, setChangeStartCoords] = useState(null);
  const [storyboardAtChangeStart, setStoryboardAtChangeStart] = useState(null);
  const [isCompStoryboardCreated, setIsCompStoryboardCreated] =
    useState<boolean>(false);

  const changeTypeRef = useRef(changeType);
  useEffect(() => {
    changeTypeRef.current = changeType;
  }, [changeType]);

  // If true, this means the storyboard is being hovered over another storyboard it can be dropped into
  const [droppingStoryboard, setDroppingStoryboard] = useState(false);

  // For drawing new storyboards or divs inside storyboards
  // Important, because the drawingObject is throttled, the drawingObjectRef must be
  // completely up to date instantaneously, so we have to set it directly
  const [drawingObject, setDrawingObjectThrottled] = useThrottledState(
    null,
    25
  );
  const drawingObjectRef = useRef(null);
  const setDrawingObject = (newDrawingObject) => {
    drawingObjectRef.current = newDrawingObject;
    setDrawingObjectThrottled(newDrawingObject);
  };

  const [storyboardBeingCreated, setStoryboardBeingCreated] = useState(null); // When finished drawing but storyboard is still being created

  const [dragToSelect, setDragToSelectThrottled] = useThrottledState(null, 25);
  const dragToSelectRef = useRef(null);
  const setDragToSelect = (newDragToSelect) => {
    dragToSelectRef.current = newDragToSelect;
    setDragToSelectThrottled(newDragToSelect);
  };

  const canvasDivRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const iframesRef = useRef({});

  // For dragging/dropping
  const [dragOverNewCanvasOutlineX, setDragOverNewCanvasOutlineX] =
    useState(null);
  const [dragOverNewCanvasOutlineY, setDragOverNewCanvasOutlineY] =
    useState(null);

  // When creating a new storyboard -> can set the HTML instantly
  const storyboardIdToSetHTML = useCanvasStore(
    (store) => store.storyboardIdToSetHTML
  );
  const setStoryboardIdToSetHTML = useCanvasStore(
    (store) => store.setStoryboardIdToSetHTML
  );
  const addStoryboardIdToSetHTML = useCanvasStore(
    (store) => store.addStoryboardIdToSetHTML
  );

  const mainCanvas = useActiveMainCanvas();

  const canvasComponentKey = useCanvasStore(
    (store) => store.canvasComponentKey
  );

  // @ts-expect-error TS2538
  const canvasComponent = componentKeyToComponent?.[canvasComponentKey];
  const canvasesLoaded = useSelector(
    (store: any) => store.supabaseStore.canvasesLoadStatus === "succeeded"
  );
  const subCanvasesLoaded = useSelector(
    (store: any) => store.supabaseStore.subCanvasesLoadStatus === "succeeded"
  );

  const setProjectLaunchStatus = useCodebaseStore(
    (state) => state.setProjectLaunchStatus
  );

  // Throttled events
  const throttledSetHoveredElement = useThrottledSetHoveredElement();
  const throttledSendSyntheticMouseOverEvent =
    useThrottledSendSyntheticMouseOverEvent();
  const throttledSyntheticMouseMove = useThrottledSyntheticMouseMove();

  const changeStoryboardRef = useRef<Storyboard | null>(null);
  useEffect(() => {
    // @ts-expect-error TS2322
    changeStoryboardRef.current = activeCanvas?.storyboards?.find?.(
      (storyboard) => storyboard?.id === changeStoryboardId
    );
  }, [activeCanvas, changeStoryboardId]);

  const selectedElementHTML = useSelector(
    (state: any) => state.iframe.selectedElementHTML
  );

  const selectedPathIsParentOfHovered = useMemo(() => {
    return (
      // Same storyboard
      hoveredElement.storyboardId === selectedElement.storyboardId &&
      // The selected element is known in the codebase
      selectedElement.isKnownElement() &&
      // The hovered element has a unique path and is a child of the selected element
      selectedElement?.isParentOf(hoveredElement)
    );
  }, [selectedElement, hoveredElement]);
  const selectedPathIsParentOfHoveredRef = useRef(
    selectedPathIsParentOfHovered
  );
  useEffect(() => {
    selectedPathIsParentOfHoveredRef.current = selectedPathIsParentOfHovered;
  }, [selectedPathIsParentOfHovered]);

  const menuLeft = useCanvasStore((state) => state.menuLeft);
  const menuTop = useCanvasStore((state) => state.menuTop);
  const setMenu = useCanvasStore((state) => state.setMenu);

  const storyboardIdToStatus = useCanvasStore(
    (state) => state.storyboardIdToStatus
  );

  const sendMessageToIFrame = useIframeStore(
    (store) => store.sendMessageToIFrame
  );

  const updateMyPresence = useUpdateMyPresence();
  const { zoomPerc } = useCanvasPos();

  const showModal = useSelector((state: any) => state.globalSettings.showModal);
  const modalActive = showModal !== ModalType.None;

  const { options: typographyOptions, defaultOption: defaultTypography } =
    useTypographyPresets();
  const selectedTypographyOption = useSelector(
    (state: any) => state.globalSettings.selectedTypographyOption
  );
  const selectedTypographyOptionToUse = useMemo(() => {
    return (
      selectedTypographyOption || typographyOptions[0] || defaultTypography
    );
  }, [selectedTypographyOption, typographyOptions]);

  const { options: layoutOptions } = useLayoutPresets();
  const selectedLayoutOption = useSelector(
    (state: any) => state.globalSettings.selectedLayoutOption
  );
  const selectedLayoutOptionToUse: LayoutPreset = useMemo(() => {
    return selectedLayoutOption || layoutOptions[0];
  }, [selectedLayoutOption, layoutOptions]);

  /********************************************************************************
   * Custom for additional functionality
   ********************************************************************************/

  // Add in copy/paste functionality
  const {
    handleImagePaste,
    SideEffectComponentInstance: CopyPasteSideEffectInstance,
  } = useCopyPaste();

  const {
    onIframeDragStart,
    onIframeDragEnd,
    onIframeDragCancel,
    isDraggingOutOfStoryboard,
    isDragging,
    triggerEndDrag,
    triggerStartDrag,
    triggerCancelDrag,
    SideEffectComponentInstance: IFrameDragSideEffectInstance,
    DragOutOutlineInstance,
  } = useIframeDrag();

  const isDraggingOutOfStoryboardRef = useRef(isDraggingOutOfStoryboard);
  useEffect(() => {
    isDraggingOutOfStoryboardRef.current = isDraggingOutOfStoryboard;
  }, [isDraggingOutOfStoryboard]);

  const onIframeMessage = (e, storyboardId: string) => {
    if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.DRAG_START_EVENT) {
      onIframeDragStart(e.data.event, storyboardId, e.data.outerHTML);
    } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.DRAG_END_EVENT) {
      onIframeDragEnd(e.data.event, storyboardId);
    } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.DRAG_CANCEL_EVENT) {
      onIframeDragCancel(e.data.event, storyboardId);
    }
  };

  const setStoryboardIdToStatus = useCanvasStore(
    (store) => store.setStoryboardIdToStatus
  );

  const {
    onLoadIFrame,
    SideEffectComponentInstance: IframePostMessagingSideEffectInstance,
  } = useIframePostMessaging(onIframeMessage, canvasDivRef, iframesRef);

  const onKeyDown = useOnKeyDown();
  const onKeyUp = useOnKeyUp();
  const onMouseDown = useOnMouseDown();
  const onMouseUp = useOnMouseUp();

  const onMouseMove = useOnMouseMove(canvasDivRef, iframesRef);

  const setNewLayout = useLayoutStore((state) => state.setNewLayout);
  const [layoutUpdated, setLayoutUpdated] = useState(false);
  const { loadComponent, installDepsForComponent } = useReactComponentHandler();

  /********************************************************************************
   * Create canvas/subcanvas if it doesn't exist
   ********************************************************************************/

  /**
   * Create storyboard for main canvas if there aren't any
   */
  useEffect(() => {
    if (!mainCanvas || !project || !supabaseClient) {
      return;
    }

    if (mainCanvas.id !== canvasId) {
      return;
    }

    if (!componentId) {
      // Create only if there are no storyboards & there was never an application storyboard (that was deleted)
      if (
        mainCanvas.storyboards?.filter((sb) => !sb.deleted && !sb.preCreated)
          ?.length ||
        // User explicitly deleted, don't re-create
        mainCanvas.storyboards?.find(
          (sb) => sb.type === STORYBOARD_TYPES.APPLICATION && sb.deleted
        )
      ) {
        return;
      }
    } else {
      if (
        mainCanvas.storyboards?.filter((sb) => !sb.deleted && !sb.preCreated)
          ?.length
      ) {
        return;
      }

      /**
       * Set the default view to Design mode when a component is opened
       * from the ReactComponents page
       */
      if (!layoutUpdated) {
        // @ts-expect-error TS2345
        setNewLayout(null, LayoutMode.Design);
        setLayoutUpdated(true);
      }
    }

    const createMainStoryboard = async () => {
      // Storyboard has not been created
      let initialSize;
      if (project?.framework === ReactFramework.EXPO) {
        initialSize = DEFAULT_MOBILE_SCREEN_SIZE;
      } else {
        initialSize = DEFAULT_SCREEN_SIZE;
      }

      // Double check local state is equal to the DB state
      const { data: dbCanvas } = await supabaseClient
        .from("canvases")
        .select("*")
        .eq("id", canvasId)
        .single();

      const localStoryboardIds = new Set(
        mainCanvas.storyboards?.map((sb) => sb.id) || []
      );
      const dbStoryboardIds = new Set(
        (dbCanvas?.storyboards as Storyboard[])?.map((sb) => sb.id) || []
      );

      let localMatchesDb = true;
      if (localStoryboardIds.size !== dbStoryboardIds.size) {
        localMatchesDb = false;
      }

      for (const storyboardId of localStoryboardIds) {
        if (!dbStoryboardIds.has(storyboardId)) {
          localMatchesDb = false;
        }
      }

      if (!localMatchesDb) {
        // Set the local storyboards to the DB storyboards
        // But only if there are storyboards in the DB
        if ((dbCanvas?.storyboards as any[])?.length && dbCanvas) {
          dispatch(onCanvasesSnapshot({ newItems: [dbCanvas] }));
        }
        return;
      }

      const newCanvas = {
        ...mainCanvas,
        storyboards: [
          ...(mainCanvas.storyboards || []),
          {
            id: uuidv4().toString(),
            type: STORYBOARD_TYPES.APPLICATION,
            width: initialSize.width,
            height: initialSize.height,
            x: -initialSize.width / 2,
            y: -initialSize.height / 2,
            zIndex: 0,
            fitCanvasToStoryboard: 0.6,
          },
        ],
      };

      newCanvas.last_updated = new Date().toISOString();

      // Immediately update the local store
      dispatch(onCanvasesSnapshot({ newItems: [newCanvas] }));

      await supabaseClient
        .from("canvases")
        .update({
          storyboards: newCanvas.storyboards,
          last_updated: newCanvas.last_updated,
        })
        .eq("id", newCanvas.id);
    };

    /**
     * Create a new storyboard for a component while opening
     * from ReactComponents
     */
    const createComponentStoryboard = async () => {
      if (typeof componentId !== "string") {
        return;
      }

      const componentIdInteger = Number.parseInt(componentId);

      if (Number.isNaN(componentIdInteger)) {
        return;
      }

      // Double check canvas has no storyboards
      const { data: canvasData } = await supabaseClient
        .from("canvases")
        .select("*")
        .eq("id", canvasId)
        .single();

      if (!canvasData?.storyboards) {
        return;
      }

      const { data, error } = await supabaseClient
        .schema("reactcomponents")
        .from("components")
        .select("*")
        .eq("id", componentIdInteger)
        .single();

      if (error) {
        console.error("Error: Couldn't find component", error);
        toast.error("Couldn't find component");
        return;
      }

      if (!data) {
        toast.error("Couldn't find component");
        return;
      }

      try {
        const initialSize = DEFAULT_SCREEN_SIZE;
        const x = -initialSize.width / 2;
        const y = -initialSize.height / 2;
        // Create a new storyboard
        const newStoryboard = initializeStoryboard(x, y, 1200, 800);
        await createStoryboard({
          storyboard: newStoryboard,
          fileContentsToCreateWith: data.contents,
          actionText: "React Component",
        });

        setIsCompStoryboardCreated(true);
        if (prompt && typeof prompt === "string") {
          const updatedPrompt = `Apply this change to the code in ${newStoryboard.name}: ${prompt}`;
          track("AI_PROMPT_REQUESTED", {
            type: "AI_CHANGE_FROM_REACTCOMPONENTS",
            prompt: updatedPrompt,
          });
          setSendChatMessage({
            sendChatMessage: updatedPrompt,
            resetToggles: "all",
          });
        }

        setSearchParams((prev) => {
          const newParams = {
            ...prev,
          };
          delete newParams["prompt"];
          delete newParams["componentId"];
          return newParams;
        });
      } catch (error) {
        console.error("Couldn't create a new storyboard: ", error);
        toast.error("Couldn't create a new storyboard: ", error);
      }
    };

    if (!componentId) {
      createMainStoryboard();
    } else if (!isCompStoryboardCreated) {
      createComponentStoryboard();
    }
  }, [
    mainCanvas?.storyboards,
    mainCanvas,
    project,
    componentId,
    projectLaunchStatus,
    prompt,
    supabaseClient,
  ]);

  /**
   * Creates new subcanvases for a component.
   *
   * Update story subcanvas if tree elements have stories that are not in the subcanvas.
   * Add new stories to the subcanvas.
   */
  useEffect(() => {
    // Wait for git to load as well
    if (!gitStatus) {
      return;
    }

    if (!canvasComponentKey) {
      return;
    }

    if (!canvasesLoaded || !subCanvasesLoaded) {
      return;
    }

    if (!project) {
      return;
    }

    const updateOrCreateStoryboardsForComponent = async () => {
      // Need to wait for canvas to be created first
      if (!mainCanvas || !supabaseClient) {
        return;
      }

      let canvasToUpdate: any = activeCanvas
        ? {
            ...activeCanvas,
          }
        : null;
      let isNewCanvas = false;

      if (!canvasToUpdate) {
        // Create a new canvas if it doesn't exist
        // This is a double-check to make sure a canvas doesn't already exist
        const { data: canvases, error } = await supabaseClient
          .from("sub_canvases")
          .select()
          .eq("canvas_id", canvasId)
          .eq("component_id", canvasComponentKey);

        if (error) {
          console.error("Error fetching canvas", error);
          toast.error(
            "Error fetching canvas, please refresh the page to try again."
          );
          return;
        }

        if (canvases?.length) {
          canvasToUpdate = {
            ...canvases[0],
          };
        } else {
          // Create a new canvas
          canvasToUpdate = {
            id: uuidv4().toString(),
            type: "COMPONENT",
            canvas_id: canvasId,
            component_id: canvasComponentKey,
            storyboards: [],
            assigned_port: null,
            assigned_user_id: null,
            container_id: null,
            current_branch: null,
            deleted_at: null,
            annotations: [],
          };

          isNewCanvas = true;

          // Show that we are creating the canvas
          await supabaseClient
            .from("project_loading_progress")
            .update({
              last_updated: new Date().toISOString(),
              initialized_env: false,
            })
            .eq("canvas_id", canvasId);

          triggerRefetchProjectLoadingProgress?.();

          setProjectLaunchStatus("loading");
        }
      }

      const component = componentKeyToComponent[canvasComponentKey];

      const stories = getStoriesForComponent(
        project,
        treeElementLookup,
        component
      );

      const storyboardsForStoryTypes = canvasToUpdate.storyboards.filter(
        (storyboard) => {
          return (
            storyboard.type === STORYBOARD_TYPES.STORY &&
            storyboard.deleted !== true &&
            !storyboard.preCreated &&
            !storyboard.isPlaceholder
          );
        }
      );

      const clearTsInSbs = new Set();

      const storyboardsToDelete = storyboardsForStoryTypes.filter(
        (storyboard) => {
          const needToDelete =
            storyboard.saved &&
            !stories.find(
              (story) =>
                storyboard.name === story.componentName &&
                storyboard.savedCodebaseId === story.codebaseId
            );

          if (storyboard.recentlySavedTs) {
            // Allow 10 seconds for the tree elements to refresh
            if (
              needToDelete &&
              Date.now() - Date.parse(storyboard.recentlySavedTs) < 1000 * 10
            ) {
              return false;
            } else {
              // It has been found, clear the recentlySavedTs state
              clearTsInSbs.add(storyboard.id);
              return false;
            }
          }

          return needToDelete;
        }
      );

      const storiesWithoutStoryboards = stories.filter((story) => {
        return !storyboardsForStoryTypes.find(
          (storyboard) =>
            storyboard.name === story?.componentName &&
            (storyboard.savedCodebaseId === story?.codebaseId ||
              storyboard.saving)
        );
      });

      // Nothing to update
      if (
        !storiesWithoutStoryboards.length &&
        !isNewCanvas &&
        !storyboardsToDelete.length
      ) {
        return;
      }

      // Remove any storyboards that are no longer needed
      canvasToUpdate.storyboards = canvasToUpdate.storyboards.map((sb: any) => {
        if (!storyboardsToDelete.find((testSb) => testSb.id === sb.id)) {
          if (clearTsInSbs.has(sb.id)) {
            return {
              ...sb,
              recentlySavedTs: null,
            };
          }

          return {
            ...sb,
          };
        }

        return {
          ...sb,
          deleted: true,
          deleted_at: new Date().toISOString(),
        };
      });

      const storyIdToSize = {};
      await Promise.all(
        storiesWithoutStoryboards.map(async (story) => {
          const imageUrl = getImageUrlForElement(metadata, story);
          if (!imageUrl) {
            return;
          }

          const img = new Image();
          img.crossOrigin = "anonymous";
          img.src = imageUrl;

          const imageLoadPromise = new Promise<{
            width?: number;
            height?: number;
          }>((resolve, reject) => {
            img.onload = () => {
              resolve({
                width: img.naturalWidth,
                height: img.naturalHeight,
              });
            };

            img.onerror = (
              e,
              source?: string,
              lineno?: number,
              colno?: number,
              error?: Error
            ) => {
              console.error("ERROR", e, error);
              resolve({});
            };
          });

          const { width, height } = await imageLoadPromise;

          if (width !== undefined && height !== undefined) {
            storyIdToSize[story.codebaseId] = {
              width: Math.max(width, 16),
              height: Math.max(height, 16),
            };
          }
        })
      );

      let currentX = 0;
      let averageWidth = 0;
      let averageHeight = 0;
      let totalNum = 0;

      canvasToUpdate.storyboards.forEach((sb) => {
        if (sb.preCreated || sb.deleted || sb.type !== STORYBOARD_TYPES.STORY) {
          return;
        }

        currentX = Math.max(sb.x + sb.width + 100, currentX);
        averageWidth += sb.width;
        averageHeight += sb.height;
        totalNum += 1;
      });

      Object.values(storyIdToSize).forEach(({ width, height }: any) => {
        averageWidth += width;
        averageHeight += height;
        totalNum += 1;
      });

      if (!totalNum) {
        // Case where there are no storyboards and there are no images for those storyboards
        averageWidth = 500;
        averageHeight = 500;
      } else {
        averageWidth = Math.round(averageWidth / totalNum);
        averageHeight = Math.round(averageHeight / totalNum);
      }

      canvasToUpdate.storyboards = canvasToUpdate.storyboards.concat(
        storiesWithoutStoryboards.map((story, index): Storyboard => {
          const { width, height } = storyIdToSize[story.codebaseId] || {};

          const widthToUse = width || averageWidth;
          const heightToUse = height || averageHeight;

          const toReturn: Storyboard = {
            id: uuidv4().toString(),
            name: story.componentName,
            type: STORYBOARD_TYPES.STORY,
            width: widthToUse,
            height: heightToUse,
            x: currentX,
            y: -heightToUse / 2,
            zIndex: 0,
            saved: true,
            created: true,
            created_at: new Date(),
            fullFilePath: story.filename,
            savedCodebaseId: story.codebaseId,
          };

          currentX += widthToUse + 100;

          return toReturn;
        })
      );
      canvasToUpdate.last_updated = new Date();

      // Immediately update the local store
      dispatch(onSubCanvasesSnapshot({ newItems: [canvasToUpdate] }));

      if (isNewCanvas) {
        await supabaseClient.from("sub_canvases").insert([canvasToUpdate]);

        // Mark env as created
        await supabaseClient
          .from("project_loading_progress")
          .update({
            last_updated: new Date().toISOString(),
            initialized_env: true,
          })
          .eq("canvas_id", canvasId);

        // Reset the loading state
        setProjectLaunchStatus("succeeded");

        triggerRefetchProjectLoadingProgress?.();

        if (stories?.length) {
          // If inserting a new canvas with stories, reload all files
          // to generate the routes for the dynamic storyboards
          const filesToLoad = Array.from(
            new Set(stories.map((story) => story.filename))
          );

          await loadCodebase({
            filesToLoad,
          });
        }
      } else {
        await supabaseClient
          .from("sub_canvases")
          .update({
            storyboards: canvasToUpdate.storyboards,
            last_updated: canvasToUpdate.last_updated,
          })
          .eq("id", canvasToUpdate.id);
      }

      // After storyboards are created/updated, enqueue story components that need metadata generation
      if (stories?.length) {
        const storyCodebaseIds = stories
          .filter((story) => {
            // Only enqueue if metadata doesn't already exist
            const fileHash = story.fileHash;
            return fileHash && !metadata[fileHash];
          })
          .map((story) => story.codebaseId);

        if (storyCodebaseIds.length > 0) {
          dispatch(enqueueElementsForScreenshotGeneration(storyCodebaseIds));
        }
      }
    };

    updateOrCreateStoryboardsForComponent();
  }, [
    canvasesLoaded,
    gitStatus,
    canvasId,
    subCanvasesLoaded,
    activeCanvas?.id,
    canvasComponentKey,
    treeElementLookup,
  ]);

  /********************************************************************************
   * Hooks for canvas events
   ********************************************************************************/

  useEffect(() => {
    // Note: The behavior expects any context menu action to select the element the action is being applied to
    if (!showContextMenu) {
      return;
    }

    if (!requestedContextPosition || !selectedElement) {
      return;
    }

    const { callsite, event } = requestedContextPosition;

    let x = event.clientX;
    let y = event.clientY;

    if (callsite == "iframe") {
      let resolved = normalizeToIFrameCoordinates(
        iframesRef,
        x,
        y,
        selectedElement.storyboardId
      );
      x = resolved.x;
      y = resolved.y;
    }

    setMenu(x, y);
  }, [requestedContextPosition, selectedElement, showContextMenu]);

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const minZIndex = useMemo(() => {
    return activeCanvas?.storyboards
      ?.map((storyboard: any) => storyboard.zIndex)
      ?.reduce((a: any, b: any) => Math.min(a, b), Infinity);
  }, [activeCanvas]);

  const maxZIndex = useMemo(() => {
    return activeCanvas?.storyboards
      ?.map((storyboard: any) => storyboard.zIndex)
      ?.reduce((a: any, b: any) => Math.max(a, b), 0);
  }, [activeCanvas]);

  const onDrop = async (e: any) => {
    // Create a fake loading storyboard
    const { x: pointOnCanvasX, y: pointOnCanvasY } =
      getPointOnCanvasFromInsideIframe(
        e.clientX,
        e.clientY,
        canvasDivRef,
        iframesRef
      );

    setIsLoading(true);
    try {
      let codebaseIdToAddTo = null;

      const dragSource = e.dataTransfer.getData("tempo/dragSource");

      // Nav drag & drop is handled the same way as on mouse up (logic in the useIframeDrag module)
      if (dragSource === "nav") {
        triggerEndDrag();
        return;
      }

      const addIsTemplateStory =
        e.dataTransfer.getData("tempo/isTemplateStory") === "true";

      const addCodebaseId = e.dataTransfer.getData("tempo/codebaseId");
      let propsToSet = null;
      try {
        propsToSet = JSON.parse(e.dataTransfer.getData("tempo/propsToSet"));
      } catch (e) {}

      const addNativeTag = e.dataTransfer.getData("tempo/nativeTag");
      const contents = e.dataTransfer.getData("tempo/contents");

      let staticData = null;
      try {
        staticData = JSON.parse(e.dataTransfer.getData("tempo/static"));
      } catch (e) {}

      // Check for community component data
      const reactComponentId = e.dataTransfer.getData("tempo/reactComponentId");

      let fileContents, instantUpdate;
      let extraPathToContents: Record<string, string> = {};

      if (contents) {
        fileContents = contents;
      } else if (staticData) {
        // @ts-expect-error TS2339
        fileContents = createDummyComponent(staticData.jsx, project.framework);
        // @ts-expect-error TS2339
        instantUpdate = staticData.instantUpdate;
      } else if (reactComponentId) {
        toast.info("Loading react component...");

        useCanvasStore.getState().setLoadingStoryboard({
          id: "loading-" + Date.now(),
          x: pointOnCanvasX - 100, // Center the loading storyboard
          y: pointOnCanvasY - 100,
          width: 200,
          height: 200,
        });
        const result = await loadComponent(reactComponentId);
        useCanvasStore.getState().setLoadingStoryboard(null);

        if (!result?.success) {
          // most likely user cancelled, other error states handled within
          return;
        }
        fileContents = result.contents;
        extraPathToContents = result.extraPathToContents || {};

        const { success, nonShadcnFiles } = await installDepsForComponent(
          result.componentData
        );

        if (!success) {
          // user cancelled
          return;
        }

        extraPathToContents = nonShadcnFiles || {};
      }

      const selectedPathIsStoryboardOfHovered =
        hoveredElement.storyboardId === selectedElement.storyboardId &&
        selectedElement.isStoryboard();

      const files = e.dataTransfer.files;
      let imageBlob = null;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.startsWith("image/")) {
          imageBlob = file; // File object is a Blob
          break;
        }
      }

      // Validation

      const isDraggingIntoNewCanvas =
        dragOverNewCanvasOutlineX !== null &&
        dragOverNewCanvasOutlineY !== null;

      if (imageBlob) {
        if (!isDraggingIntoNewCanvas) {
          toast.info("You can only drag image files into new storyboards");
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        const { x: pointOnCanvasX, y: pointOnCanvasY } =
          getPointOnCanvasFromInsideIframe(
            e.clientX,
            e.clientY,
            canvasDivRef,
            iframesRef
          );

        try {
          await handleImagePaste(imageBlob, {
            position: {
              x: pointOnCanvasX,
              y: pointOnCanvasY,
            },
          });
        } catch (error) {
          console.error("Couldn't handle image paste:" + error);
        }

        return;
      }

      const elementKeyToNavNodeLookup =
        useCanvasStore.getState().elementKeyToNavNodeLookup;

      // Three scenarios
      // 1) Dropping onto the canvas -> create a new storyboard
      if (isDraggingIntoNewCanvas) {
        const { x: pointOnCanvasX, y: pointOnCanvasY } =
          getPointOnCanvasFromInsideIframe(
            e.clientX,
            e.clientY,
            canvasDivRef,
            iframesRef
          );

        if (!activeCanvas) {
          toast.error("Could not find active canvas");
          setDragOverNewCanvasOutlineX(null);
          setDragOverNewCanvasOutlineY(null);
          return;
        }

        const storyboard = getStoryboardForPoint(
          activeCanvas,
          pointOnCanvasX,
          pointOnCanvasY
        );

        if (storyboard) {
          toast.error("Could not find element to insert into");
          setDragOverNewCanvasOutlineX(null);
          setDragOverNewCanvasOutlineY(null);
          return;
        }

        // TODO: Create a new storyboard -> create a function for it
        // Then add the component into the storyboard top level codebase ID
        const width = addIsTemplateStory
          ? DRAG_IN_LARGER_WIDTH
          : DRAG_IN_INITIAL_WIDTH;
        const height = addIsTemplateStory
          ? DRAG_IN_LARGER_HEIGHT
          : DRAG_IN_INITIAL_HEIGHT;

        let contentsArg = undefined;
        let componentToAddArg = undefined;

        if (fileContents) {
          contentsArg = fileContents;
        } else if (addCodebaseId) {
          // @ts-expect-error TS2322
          componentToAddArg = {
            componentId: addCodebaseId,
            nativeTag: addNativeTag,
            propsToSet,
          };
        }

        const newStoryboard = initializeStoryboard(
          dragOverNewCanvasOutlineX - width / 2,
          dragOverNewCanvasOutlineY - height / 2,
          width,
          height
        );

        await createStoryboard({
          storyboard: newStoryboard,
          fileContentsToCreateWith: contentsArg,
          componentToAdd: componentToAddArg,
          extraPathToContents:
            Object.keys(extraPathToContents).length > 0
              ? extraPathToContents
              : undefined,
          actionText: "Dragged Component",
        });

        e.preventDefault();

        setDragOverNewCanvasOutlineX(null);
        setDragOverNewCanvasOutlineY(null);

        // Don't want to add JSX to it since it's already added
        return;
      }

      // 2) Hovered path is set and it is a subset of the selected (or equal)
      // Insert into the selected codebase ID
      if (selectedPathIsParentOfHoveredRef.current) {
        const navTreeNode = elementKeyToNavNodeLookup[selectedElement.getKey()];
        // @ts-expect-error TS2322
        codebaseIdToAddTo = navTreeNode.tempoElement.codebaseId;
      }

      // 3) The storyboard is selected, so just add it to that directly
      else if (selectedPathIsStoryboardOfHovered) {
        // Insert into the selected storyboard ID
        // @ts-expect-error TS2322
        codebaseIdToAddTo = activeCanvas.storyboards?.find(
          (storyboard) => storyboard.id === selectedElement.storyboardId
        )?.storyboardComponentCodebaseId;
      }

      // Hovered element is a storyboard
      else if (hoveredElement.isStoryboard()) {
        // Insert into the hovered storyboard ID
        // @ts-expect-error TS2322
        codebaseIdToAddTo = activeCanvas.storyboards?.find(
          (storyboard) => storyboard.id === hoveredElement.storyboardId
        )?.storyboardComponentCodebaseId;

        // Select the storyboard
        dispatch(setSelectedElementKey(hoveredElement.getKey()));
      }

      // 3) There is a hovered codebase ID that is not part of the selected path, set the codebase to add to as being the selected codebase ID
      else if (
        // Need to check both explicitly, as the truthiness of hovered element has custom values
        hoveredElement.isKnownElement()
      ) {
        const navTreeNode = elementKeyToNavNodeLookup[hoveredElement.getKey()];
        // @ts-expect-error TS2322
        codebaseIdToAddTo = navTreeNode.tempoElement.codebaseId;
        dispatch(setSelectedElementKey(hoveredElement.getKey()));
      } else {
        // Unknown case
        toast.error(
          "Could not find element to insert into or create a new storyboard"
        );
        setDragOverNewCanvasOutlineX(null);
        setDragOverNewCanvasOutlineY(null);
        return;
      }

      e.preventDefault();

      if (!addCodebaseId && !addNativeTag && !fileContents) {
        toast.error("Dropped element cannot be added");
        console.error(
          "Dropped element cannot be added, no codebase ID native tag or fileContents"
        );
        return;
      }

      executeCommand(
        new AddJsxChange({
          // @ts-expect-error TS2322
          codebaseIdToAddTo,
          addCodebaseId,
          addNativeTag,
          // @ts-expect-error TS2322
          propsToSet,
          fileContentsToSourceFrom: fileContents,
          htmlForInstantUpdate: instantUpdate,
        })
      );
    } catch (error) {
      console.error("Error in onDrop:", error);
      // toast.error("An error occurred while processing the drop: " + error);
    } finally {
      useCanvasStore.getState().setLoadingStoryboard(null);
      setIsLoading(false);
    }
  };

  const createSyntheticOutlines = useCallback(
    (clientX: number, clientY: number) => {
      if (!activeCanvasRef.current) {
        return;
      }

      const {
        storyboardToNavTree,
        storyboardIdToStatus: _storyboardIdToStatus,
      } = useCanvasStore.getState();

      // Offset the point
      const clientXToUse = clientX;
      const clientYToUse = clientY;

      const { x: pointOnCanvasX, y: pointOnCanvasY } =
        getPointOnCanvasFromInsideIframe(
          clientXToUse,
          clientYToUse,
          canvasDivRef,
          iframesRef
        );

      const storyboard: Storyboard | null = getStoryboardForPoint(
        activeCanvasRef.current,
        pointOnCanvasX,
        pointOnCanvasY,
        changeTypeRef.current === StoryboardChangeType.MOVE
          ? changeStoryboardIdRef.current ?? undefined
          : undefined
      );

      if (!storyboard) {
        setDroppingStoryboard(false);
        // @ts-expect-error TS2345
        setDragOverNewCanvasOutlineX(pointOnCanvasX);
        // @ts-expect-error TS2345
        setDragOverNewCanvasOutlineY(pointOnCanvasY);

        if (!hoveredElementRef.current.isEmpty()) {
          sendMessageToIFrame({
            type: "executeFunction",
            id: uuidv4(),
            payload: {
              functionName: "clearHoveredOutlines",
              args: [],
            },
          });
        }

        return;
      } else {
        setDragOverNewCanvasOutlineX(null);
        setDragOverNewCanvasOutlineY(null);
      }

      // Highlight the storyboard itself
      if (
        storyboardToNavTree?.[storyboard.id]?.tempoElement.codebaseId ===
          EMPTY_TREE_CODEBASE_ID ||
        _storyboardIdToStatus[storyboard.id]?.status === "frozen"
      ) {
        const storyboardTempoElement = TempoElement.forStoryboard(
          storyboard.id
        );

        if (
          changeTypeRef.current === StoryboardChangeType.MOVE ||
          isDraggingOutOfStoryboardRef.current
        ) {
          // Unfreeze the storyboard
          setStoryboardIdToStatus(storyboard.id, {
            status: "loading",
            last_updated: new Date(),
          });
        }

        if (changeTypeRef.current === StoryboardChangeType.MOVE) {
          setDroppingStoryboard(true);
        }

        throttledSetHoveredElement.current(storyboardTempoElement);
        return;
      }

      if (
        storyboard?.zIndex > -1 &&
        pointOnCanvasX !== null &&
        pointOnCanvasY !== null
      ) {
        const pointInStoryboardX = Math.round(pointOnCanvasX - storyboard.x);
        const pointInStoryboardY = Math.round(pointOnCanvasY - storyboard.y);

        if (changeTypeRef.current === StoryboardChangeType.MOVE) {
          setDroppingStoryboard(true);
        }

        throttledSendSyntheticMouseOverEvent.current(
          storyboard.id,
          pointInStoryboardX,
          pointInStoryboardY,
          changeTypeRef.current
        );

        throttledSyntheticMouseMove.current(
          storyboard.id,
          pointInStoryboardX,
          pointInStoryboardY,
          1 // pressed down
        );
      }
    },
    []
  );

  const throttledCreateSyntheticOutlinesRef = useRef(
    _.throttle(createSyntheticOutlines, 100, { leading: true, trailing: false })
  );

  const onDragOver = (e: any) => {
    onCanvasMoveEvent(e);

    e.preventDefault();

    throttledCreateSyntheticOutlinesRef.current(e.clientX, e.clientY);
  };

  const onDragEnter = (e: any) => {
    if (draggingComponent === "nav" && !isDragging) {
      triggerStartDrag();
    }
  };

  const onDragLeave = (e: any) => {
    setDragOverNewCanvasOutlineX(null);
    setDragOverNewCanvasOutlineY(null);

    // If leaving over a storyboard, allow the drag to continue
    const { x: pointOnCanvasX, y: pointOnCanvasY } =
      getPointOnCanvasFromInsideIframe(
        e.clientX,
        e.clientY,
        canvasDivRef,
        iframesRef
      );

    if (!activeCanvas) {
      return;
    }

    const storyboard = getStoryboardForPoint(
      activeCanvas,
      pointOnCanvasX,
      pointOnCanvasY
    );

    if (draggingComponent === "nav" && !storyboard) {
      triggerCancelDrag();
    }
  };

  useEffect(() => {
    if (changeType) {
      dispatch(setIgnoreCanvasUpdates(true));
    } else {
      const timeout = setTimeout(
        () => dispatch(setIgnoreCanvasUpdates(false)),
        1000
      );
      return () => clearTimeout(timeout);
    }
  }, [changeType]);

  // Cache for performance during drag operations
  const maxZIndexRef = useRef(0);
  const storyboardIndexRef = useRef(-1);

  const [draggedStoryboard, setDraggedStoryboard] = useState<Storyboard | null>(
    null
  );

  const startStoryboardChange = useCallback(
    (
      storyboard: any,
      changeType: StoryboardChangeType,
      clientX: number,
      clientY: number
    ) => {
      setChangeStoryboardId(storyboard.id);
      setChangeType(changeType);
      setChangeStartCoords({
        // @ts-expect-error TS2353
        x: clientX,
        y: clientY,
      });
      setStoryboardAtChangeStart({
        ...storyboard,
      });

      // Pre-calculate z index and max z index
      const storyboardIndex = activeCanvas?.storyboards?.findIndex(
        (sb: Storyboard) => sb.id === storyboard.id
      );
      storyboardIndexRef.current = storyboardIndex ?? -1;

      // Pre-calculate max z-index excluding current storyboard
      const maxZIndex =
        activeCanvas?.storyboards
          ?.filter((sb: Storyboard) => sb.id !== storyboard.id)
          ?.reduce(
            (max: number, sb: Storyboard) => Math.max(max, sb.zIndex || 0),
            0
          ) ?? 0;
      maxZIndexRef.current = maxZIndex;
    },
    [activeCanvas]
  );

  const dropStoryboardIntoOtherStoryboard = useCallback(async () => {
    if (!supabaseClient) {
      console.error("Supabase client not available");
      return;
    }

    let codebaseIdToAddTo, storyboardIdToDropInto;

    const storyboardBeingDeleted = changeStoryboardRef.current;

    if (!storyboardBeingDeleted) {
      toast.error("Could not find storyboard to drop");
      return;
    }

    const elementKeyToNavNodeLookup =
      useCanvasStore.getState().elementKeyToNavNodeLookup;

    if (!hoveredElementRef.current.isEmpty()) {
      const navTreeNode =
        elementKeyToNavNodeLookup[hoveredElementRef.current.getKey()];
      codebaseIdToAddTo = navTreeNode.tempoElement.codebaseId;
      storyboardIdToDropInto = navTreeNode.tempoElement.storyboardId;

      // Drop into the hovered element
      dispatch(setSelectedElementKey(hoveredElementRef.current.getKey()));
    } else {
      const navTreeNode =
        elementKeyToNavNodeLookup[selectedElementRef.current.getKey()];
      codebaseIdToAddTo = navTreeNode.tempoElement.codebaseId;
      storyboardIdToDropInto = navTreeNode.tempoElement.storyboardId;
    }

    // Storyboard has to be live to be dropped into
    if (storyboardIdToStatus[storyboardIdToDropInto]?.status !== "live") {
      return;
    }

    let change: AnyChangeLedgerItem;
    if (
      !storyboardBeingDeleted.saved ||
      !storyboardBeingDeleted.savedCodebaseId
    ) {
      change = new AddJsxChange({
        codebaseIdToAddTo,
        fileContentsSourceFilename: storyboardBeingDeleted.fullFilePath,

        deletedStoryboardId: storyboardBeingDeleted.id,
        htmlForInstantUpdate: selectedElementHTML,
      });
    } else {
      change = new AddJsxChange({
        codebaseIdToAddTo,

        addCodebaseId: storyboardBeingDeleted.savedCodebaseId,

        deletedStoryboardId: storyboardBeingDeleted.id,
        htmlForInstantUpdate: selectedElementHTML,
      });
    }

    change.onApiReject(async () => {
      // If the API request failed, we need to undo the deleting of the storyboard
      const newStoryboards = activeCanvasRef.current?.storyboards.map(
        (sb: any) => {
          if (sb.id === storyboardBeingDeleted.id) {
            return {
              ...sb,
              deleted: false,
              deleted_at: null,
              created_at: new Date().toISOString(),
            };
          }

          return {
            ...sb,
          };
        }
      ) as Storyboard[];

      await updateCanvasStoryboards(
        supabaseClient,
        dispatch,
        newStoryboards,
        activeCanvasRef.current!
      );
    });

    executeCommand(change);

    const newStoryboards = activeCanvasRef.current?.storyboards.map(
      (sb: Storyboard) => {
        if (sb.id !== storyboardBeingDeleted.id) {
          return {
            ...sb,
          };
        }

        return {
          ...sb,
          deleted: true,
          deleted_at: new Date().toISOString(),
        };
      }
    );

    if (!newStoryboards || !activeCanvas) {
      return;
    }

    await updateCanvasStoryboards(
      supabaseClient,
      dispatch,
      newStoryboards,
      activeCanvas
    );
  }, [selectedElementHTML, supabaseClient]);

  const endDrawingObject = useCallback(
    async (objectToCreate: any) => {
      if (!objectToCreate) return;

      const isCursorText = cursorTypeRef.current === CursorType.Text;

      const conditions = useCodebaseStore.getState().conditions;
      const usingTailwind = conditions && conditions["tailwindInstalled"]?.met;

      // This case is actually drawing inside another storyboard
      if (
        objectToCreate.codebaseIdToAddTo &&
        selectedLayoutOptionToUse.id !== LayoutId.APPLICATION
      ) {
        if (isCursorText && !modalActive) {
          track("CREATE_ELEMENT_FROM_DRAW", {
            element: selectedTypographyOptionToUse.tag,
          });

          const dummyComponentContents = createDummyComponent(
            selectedTypographyOptionToUse.jsxString,
            project.framework
          );
          const instantUpdate = selectedTypographyOptionToUse.htmlInstantUpdate;

          executeCommand(
            new AddJsxChange({
              codebaseIdToAddTo: objectToCreate.codebaseIdToAddTo,
              afterCodebaseId: objectToCreate.afterCodebaseId,
              beforeCodebaseId: objectToCreate.beforeCodebaseId,
              fileContentsToSourceFrom: dummyComponentContents,
              htmlForInstantUpdate: instantUpdate,
            })
          );
        } else {
          track("CREATE_ELEMENT_FROM_DRAW", { element: "div" });

          // Add the div to the storyboard
          const tailwindClasses = `w-[${Math.abs(
            objectToCreate.width
          )}px] h-[${Math.abs(objectToCreate.height)}px]`;

          const inlineStyles = `width: ${Math.abs(
            objectToCreate.width
          )}px; height: ${Math.abs(objectToCreate.height)}px;`;

          let dummyComponentContents, instantUpdate;
          if (selectedLayoutOptionToUse) {
            dummyComponentContents = createDummyComponent(
              usingTailwind
                ? selectedLayoutOptionToUse.getJsxWithCustomClasses(
                    tailwindClasses
                  )
                : selectedLayoutOptionToUse.getHtmlWithCustomStyles(
                    inlineStyles
                  ),
              project.framework
            );
            instantUpdate =
              selectedLayoutOptionToUse.getHtmlWithCustomStyles(inlineStyles);
          }

          executeCommand(
            new AddJsxChange({
              codebaseIdToAddTo: objectToCreate.codebaseIdToAddTo,
              afterCodebaseId: objectToCreate.afterCodebaseId,
              beforeCodebaseId: objectToCreate.beforeCodebaseId,
              fileContentsToSourceFrom: dummyComponentContents,
              htmlForInstantUpdate: instantUpdate,
            })
          );
        }
        dispatch(
          setCursorType({
            cursorType: CursorType.Pointer,
          })
        );
      } else {
        // Create a new storyboard
        dispatch(
          setCursorType({
            cursorType: CursorType.Pointer,
          })
        );
        dispatch(setModal(ModalType.None));

        if (selectedLayoutOptionToUse.id === LayoutId.APPLICATION) {
          const newApplicationStoryboard = initializeStoryboard(
            objectToCreate.x +
              (objectToCreate.width < 0 ? objectToCreate.width : 0),
            objectToCreate.y +
              (objectToCreate.height < 0 ? objectToCreate.height : 0),
            Math.abs(objectToCreate.width),
            Math.abs(objectToCreate.height),
            objectToCreate.id,
            // @ts-expect-error TS2345
            null,
            null,
            false,
            null,
            true
          );

          // @ts-expect-error TS18047
          const newStoryboards = activeCanvasRef.current.storyboards.concat([
            newApplicationStoryboard,
          ]);

          await updateCanvasStoryboards(
            supabaseClient,
            dispatch,
            newStoryboards,
            // @ts-expect-error TS2345
            activeCanvasRef.current
          );

          return;
        }

        setStoryboardBeingCreated({
          ...objectToCreate,
          x:
            objectToCreate.x +
            (objectToCreate.width < 0 ? objectToCreate.width : 0),
          y:
            objectToCreate.y +
            (objectToCreate.height < 0 ? objectToCreate.height : 0),
          width: Math.abs(objectToCreate.width),
          height: Math.abs(objectToCreate.height),
        });

        const jsxContents = isCursorText
          ? usingTailwind
            ? wrapWithBlankStoryboard(
                selectedTypographyOptionToUse.jsxString,
                project.framework
              )
            : wrapWithBlankStoryboardNoTailwind(
                selectedTypographyOptionToUse.jsxString,
                project.framework
              )
          : usingTailwind
          ? selectedLayoutOptionToUse.getJsxWithCustomClasses(
              "w-screen h-screen bg-white"
            )
          : selectedLayoutOptionToUse.getHtmlWithCustomStyles(
              "width: 100vw; height: 100vh; background-color: white;"
            );

        const instantUpdateHtml = isCursorText
          ? wrapWithBlankStoryboardNoTailwind(
              selectedTypographyOptionToUse.jsxString,
              project.framework
            )
          : selectedLayoutOptionToUse.getHtmlWithCustomStyles(
              "width: 100%; height: 100%; background-color: white;"
            );

        // Cannot predict what is in the application storyboard
        addStoryboardIdToSetHTML(objectToCreate.id, instantUpdateHtml);

        const newStoryboard = initializeStoryboard(
          objectToCreate.x +
            (objectToCreate.width < 0 ? objectToCreate.width : 0),
          objectToCreate.y +
            (objectToCreate.height < 0 ? objectToCreate.height : 0),
          Math.abs(objectToCreate.width),
          Math.abs(objectToCreate.height),
          objectToCreate.id
        );

        const dummyComponentContents = createDummyComponent(
          jsxContents,
          project.framework,
          newStoryboard.name
        );

        await createStoryboard({
          storyboard: newStoryboard,
          fileContentsToCreateWith: dummyComponentContents,
          actionText: "Drawn Component",
        });

        setStoryboardBeingCreated(null);
      }
    },
    [
      initializeStoryboard,
      createStoryboard,
      selectedTypographyOptionToUse,
      modalActive,
      selectedLayoutOptionToUse,
      project,
    ]
  );

  const endAllChanges = useCallback(() => {
    setDragToSelect(null);

    // Commit dragged storyboard to Redux if it exists
    if (draggedStoryboard && activeCanvas) {
      const newStoryboards = activeCanvas.storyboards.map((sb: any) =>
        sb.id === draggedStoryboard.id ? draggedStoryboard : sb
      );

      const updateTs = new Date();

      dispatch(
        onCanvasesSnapshot({
          newItems: [
            // @ts-expect-error
            {
              ...activeCanvas,
              storyboards: newStoryboards,
              last_updated: updateTs.toISOString(),
            },
          ],
          forceUpdate: true,
        })
      );

      // Write to database
      throttleWriteCanvasRef.current(
        supabaseClient,
        dispatch,
        activeCanvas,
        newStoryboards,
        updateTs
      );

      // Clear local drag state AFTER committing to Redux
      // Use a small delay to ensure Redux update completes
      setTimeout(() => {
        setDraggedStoryboard(null);
      }, 0);
    } else {
      // Clear local drag state immediately if no dragged storyboard
      setDraggedStoryboard(null);
    }
    setChangeStoryboardId(null);
    // @ts-expect-error TS2345
    setChangeType(null);
    setChangeStartCoords(null);
    setStoryboardAtChangeStart(null);

    if (drawingObjectRef.current) {
      // Set a minimal width and height if the user selects the draw storyboard mode and clicks on the canvas instead of dragging to draw one.
      // @ts-expect-error TS2698
      const newDrawingObject = { ...drawingObjectRef.current };
      if (!newDrawingObject.dragged) {
        newDrawingObject.width = Math.max(
          DRAG_IN_INITIAL_WIDTH,
          newDrawingObject.width
        );
        newDrawingObject.height = Math.max(
          DRAG_IN_INITIAL_HEIGHT,
          newDrawingObject.height
        );
      }

      setDrawingObject(null);

      endDrawingObject(newDrawingObject);
    }

    // Drop storyboard into the other storyboard
    if (
      changeTypeRef.current === StoryboardChangeType.MOVE &&
      droppingStoryboard &&
      (selectedElementRef.current.isKnownElement() ||
        hoveredElementRef.current.isKnownElement()) &&
      // @ts-expect-error TS18047
      changeStoryboardRef.current.type !== STORYBOARD_TYPES.APPLICATION
    ) {
      dropStoryboardIntoOtherStoryboard();
    }

    triggerEndDrag();
  }, [
    draggedStoryboard,
    activeCanvas,
    dispatch,
    supabaseClient,
    endDrawingObject,
    dropStoryboardIntoOtherStoryboard,
    triggerEndDrag,
    droppingStoryboard,
  ]);

  const startDragToSelect = (e: any) => {
    if (!hoveredElement.isEmpty()) {
      return;
    }

    // Deselect all
    dispatch(setSelectedElementKey(null));

    const { x: pointOnCanvasX, y: pointOnCanvasY } =
      getPointOnCanvasFromInsideIframe(
        e.clientX,
        e.clientY,
        canvasDivRef,
        iframesRef
      );

    const newDragToSelect = {
      x: pointOnCanvasX,
      y: pointOnCanvasY,
      width: 0,
      height: 0,
    };
    setDragToSelect(newDragToSelect);
  };

  useEffect(() => {
    if (!activeCanvas?.storyboards) {
      return;
    }

    if (!dragToSelect) {
      return;
    }

    const doRectanglesOverlap = (A, B) => {
      return !(
        A.x + A.width < B.x || // A is to the left of B
        B.x + B.width < A.x || // A is to the right of B
        A.y + A.height < B.y || // A is above B
        B.y + B.height < A.y
      ); // A is below B
    };

    const storyboardsOverlappingWithDrag = activeCanvas.storyboards.filter(
      (storyboard: any) => {
        if (storyboard.deleted || storyboard.preCreated) {
          return;
        }

        const storyboardBoundingBox = {
          x: storyboard.x,
          y: storyboard.y,
          width: storyboard.width,
          height: storyboard.height,
        };

        return doRectanglesOverlap(
          {
            x:
              // @ts-expect-error TS2339
              dragToSelect.x +
              // @ts-expect-error TS2339
              (dragToSelect.width < 0 ? dragToSelect.width : 0),
            y:
              // @ts-expect-error TS2339
              dragToSelect.y +
              // @ts-expect-error TS2339
              (dragToSelect.height < 0 ? dragToSelect.height : 0),
            // @ts-expect-error TS2339
            width: Math.abs(dragToSelect.width),
            // @ts-expect-error TS2339
            height: Math.abs(dragToSelect.height),
          },
          storyboardBoundingBox
        );
      }
    );

    if (!storyboardsOverlappingWithDrag.length) {
      dispatch(setSelectedElementKey(null));
    } else if (storyboardsOverlappingWithDrag.length === 1) {
      dispatch(
        setSelectedElementKey(
          TempoElement.forStoryboard(
            storyboardsOverlappingWithDrag[0].id
          ).getKey()
        )
      );
    } else {
      dispatch(
        setAllSelectedElementKeys({
          multiSelectedElementKeys: storyboardsOverlappingWithDrag.map(
            (storyboard: any) =>
              TempoElement.forStoryboard(storyboard.id).getKey()
          ),
          selectedElementKey: TempoElement.forStoryboard(
            storyboardsOverlappingWithDrag[0].id
          ).getKey(),
        })
      );
    }
  }, [dragToSelect]);

  const startDrawingObject = useCallback(
    async (e: any) => {
      const { x: pointOnCanvasX, y: pointOnCanvasY } =
        getPointOnCanvasFromInsideIframe(
          e.clientX,
          e.clientY,
          canvasDivRef,
          iframesRef
        );

      const drawingApplicationStoryboard =
        cursorTypeRef.current === CursorType.Draw &&
        selectedLayoutOptionToUse.id === LayoutId.APPLICATION;

      const newStoryboard: any = initializeStoryboard(
        pointOnCanvasX,
        pointOnCanvasY,
        1,
        1,
        // @ts-expect-error TS2345
        null,
        null,
        null,
        null,
        null,
        drawingApplicationStoryboard
      );

      const iframeMousePos = useCanvasStore.getState().iframeMousePos;

      // Check if this new block is being drawn inside of an existing storyboard
      if (
        hoveredElementRef.current.isKnownElement() &&
        !drawingApplicationStoryboard
      ) {
        let navTreeNodeToAddTo: NavTreeNode | null;

        const elementKeyToNavNodeLookup =
          useCanvasStore.getState().elementKeyToNavNodeLookup;

        // There is a selected path that should be used as the object to add the storyboard to
        if (selectedPathIsParentOfHoveredRef.current) {
          navTreeNodeToAddTo =
            elementKeyToNavNodeLookup[selectedElementRef.current.getKey()];
        } else {
          // Use the hovered path as the one to add to
          navTreeNodeToAddTo =
            elementKeyToNavNodeLookup[hoveredElementRef.current.getKey()];

          dispatch(setSelectedElementKey(hoveredElementRef.current.getKey()));
        }

        newStoryboard.codebaseIdToAddTo =
          navTreeNodeToAddTo.tempoElement.codebaseId;

        // @ts-expect-error TS18047
        const storyboard = activeCanvasRef.current.storyboards.find(
          (storyboard: any) =>
            storyboard.id === hoveredElementRef.current.storyboardId
        );
        // Pick the index to draw in
        // Note: we can't use pageX/pageY because they are not updated when the user is in draw mode
        // @ts-expect-error TS18048
        const pointXInStoryboard = pointOnCanvasX - storyboard.x;
        // @ts-expect-error TS18048
        const pointYInStoryboard = pointOnCanvasY - storyboard.y;

        const amountScrollX = iframeMousePos
          ? iframeMousePos.pageX - iframeMousePos.clientX
          : 0;
        const amountScrolledY = iframeMousePos
          ? iframeMousePos.pageY - iframeMousePos.clientY
          : 0;

        const { expectedIndex } = getExpectedIndexForPos(
          navTreeNodeToAddTo,
          pointXInStoryboard + amountScrollX,
          pointYInStoryboard + amountScrolledY
        );

        // For the request to add later
        newStoryboard.indexToAddAt =
          expectedIndex === null
            ? navTreeNodeToAddTo.children.length
            : expectedIndex;
        newStoryboard.beforeCodebaseId =
          // @ts-expect-error TS2538
          navTreeNodeToAddTo.children[expectedIndex]?.tempoElement.codebaseId;
        newStoryboard.afterCodebaseId =
          navTreeNodeToAddTo.children[
            // @ts-expect-error TS18049
            expectedIndex - 1
          ]?.tempoElement.codebaseId;
      } else if (
        hoveredElementRef.current.isStoryboard() &&
        !drawingApplicationStoryboard
      ) {
        // Nothing selected, but trying to draw over a storyboard, add a div to it
        // @ts-expect-error TS18047
        const storyboard = activeCanvasRef.current.storyboards.find(
          (storyboard: any) =>
            storyboard.id === hoveredElementRef.current.storyboardId
        );
        const storyboardToNavTree =
          useCanvasStore.getState().storyboardToNavTree;

        newStoryboard.indexToAddAt =
          // @ts-expect-error TS2538
          storyboardToNavTree?.[storyboard?.id]?.children?.length;
        newStoryboard.codebaseIdToAddTo =
          // @ts-expect-error TS18048
          storyboard.storyboardComponentCodebaseId;

        dispatch(setSelectedElementKey(hoveredElementRef.current.getKey()));
      }

      if (cursorTypeRef.current === CursorType.Text) {
        // Set a predefined width and height
        newStoryboard.width = DEFAULT_STORYBOARD_TEXT_WIDTH;
        newStoryboard.height = DEFAULT_STORYBOARD_TEXT_HEIGHT;

        // Immediately create a new storyboard/text element
        endDrawingObject(newStoryboard);
      } else {
        setDrawingObject(newStoryboard);
      }
    },
    [endDrawingObject, initializeStoryboard, selectedLayoutOptionToUse]
  );

  const createNewVariant = async (
    x: number,
    y: number,
    width: number,
    height: number,
    name: string,
    sourceCodebaseId: string
  ) => {
    const variantStoryboard = initializeStoryStoryboard(
      x,
      y,
      width,
      height,
      name
    );

    // Create a new storyboard
    dispatch(
      setCursorType({
        cursorType: CursorType.Pointer,
      })
    );
    // @ts-expect-error TS2345
    setStoryboardBeingCreated({
      ...variantStoryboard,
    });

    const componentToAdd = treeElementLookup[sourceCodebaseId];

    await createStoryboard({
      storyboard: variantStoryboard,
      componentToAdd,
      actionText: "New Variant",
    });

    setStoryboardBeingCreated(null);
  };

  const throttledSendDrawingDiv = useSendDrawingDiv();

  /**
   * If drawing a div, send the div to the iframe for instant feedback
   */
  useEffect(() => {
    // @ts-expect-error TS2339
    if (drawingObject?.codebaseIdToAddTo && !selectedElement.isEmpty()) {
      throttledSendDrawingDiv.current(
        selectedElement.storyboardId,
        // @ts-expect-error TS2339
        drawingObject.codebaseIdToAddTo,
        // @ts-expect-error TS2339
        drawingObject.indexToAddAt,
        // @ts-expect-error TS2339
        Math.abs(drawingObject.width),
        // @ts-expect-error TS2339
        Math.abs(drawingObject.height)
      );
    }
  }, [drawingObject]);

  const cursorStyles = {
    draw: "crosshair",
    default: "auto",
    text: "text",
    edit: "text",
  };

  let cursorStyle = cursorStyles[cursorType] || cursorStyles.default;

  // Override cursor for pan mode
  const isSpacePressed = useSelector(
    (state: any) => state.globalSettings.isSpacePressed
  );
  const isMiddleMousePressed = useSelector(
    (state: any) => state.globalSettings.isMiddleMousePressed
  );

  if (isSpacePressed || cursorType === CursorType.Pan || isMiddleMousePressed) {
    cursorStyle = "grab";
  }

  const storyboardsToDraw = [
    ...(activeCanvas?.storyboards?.filter(
      (sb: any) => sb.deleted !== true && !sb.preCreated
    ) || []),
  ];

  // Use dragged storyboard if it exists (for smooth drag updates)
  if (draggedStoryboard) {
    const draggedIndex = storyboardsToDraw.findIndex(
      (sb) => sb.id === draggedStoryboard.id
    );
    if (draggedIndex !== -1) {
      storyboardsToDraw[draggedIndex] = draggedStoryboard;
    }
  }

  if (storyboardBeingCreated) {
    storyboardsToDraw.push(storyboardBeingCreated);
  }

  // Render the main canvas storyboards as well but hidden, so they don't lose their state
  const storyboardsIdsToHide = new Set<string>();
  if (activeCanvas?.id && activeCanvas?.id !== mainCanvas?.id) {
    for (const sb of mainCanvas?.storyboards || []) {
      if (!sb.deleted && !sb.preCreated) {
        storyboardsToDraw.push(sb);
        storyboardsIdsToHide.add(sb.id);
      }
    }
  }

  useEffect(() => {
    if (cursorType === CursorType.Drive) {
      sendMessageToIFrame({
        type: "executeFunction",
        payload: {
          functionName: "enableDriveMode",
          args: [],
        },
      });
    } else {
      sendMessageToIFrame({
        type: "executeFunction",
        payload: {
          functionName: "disableDriveMode",
          args: [],
        },
      });
    }
  }, [cursorType]);

  const writeCanvas = async (
    supabaseClient: any,
    dispatch: any,
    canvas: any,
    newStoryboards: any,
    updateTs: Date
  ) => {
    await updateCanvasStoryboards(
      supabaseClient,
      dispatch,
      newStoryboards,
      canvas,
      updateTs
    );
  };

  const throttleWriteCanvasRef = useRef(
    (() => {
      let isUpdating = false;

      return _.throttle(
        (
          supabaseClient: any,
          dispatch: any,
          canvas: any,
          newStoryboards: any,
          updateTs: Date
        ) => {
          if (isUpdating) {
            return;
          }

          isUpdating = true;

          writeCanvas(
            supabaseClient,
            dispatch,
            canvas,
            newStoryboards,
            updateTs
          ).then(() => {
            isUpdating = false;
          });
        },
        250,
        { trailing: true, leading: false }
      );
    })()
  );
  const throttledDispatchRef = useRef(
    _.throttle((dispatch, func, params) => {
      dispatch(func(params));
    }, 40)
  );

  /**
   * Used on the canvas pointer move, or drag when dragging
   */
  const onCanvasMoveEvent = (e) => {
    // @ts-expect-error TS2769
    onMouseMove(e, null);

    if (changeStoryboardId && changeType) {
      if (!selectedElement.isStoryboard(changeStoryboardId)) {
        dispatch(
          setSelectedElementKey(
            TempoElement.forStoryboard(changeStoryboardId).getKey()
          )
        );
      }

      const storyboardIndex = storyboardIndexRef.current;
      const storyboard = activeCanvas?.storyboards?.[storyboardIndex];

      // Early return if storyboard not found
      if (!storyboard || storyboardIndex === -1) {
        return;
      }

      // @ts-expect-error TS18047
      const deltaX = e.clientX - changeStartCoords.x;
      // @ts-expect-error TS18047
      const deltaY = e.clientY - changeStartCoords.y;

      // @ts-expect-error TS18047
      let newWidth = storyboardAtChangeStart.width;
      // @ts-expect-error TS18047
      let newHeight = storyboardAtChangeStart.height;
      // @ts-expect-error TS18047
      let newX = storyboardAtChangeStart.x;
      // @ts-expect-error TS18047
      let newY = storyboardAtChangeStart.y;

      const updateTop = () => {
        // @ts-expect-error TS18048
        newHeight -= deltaY / (zoomPerc * 0.45);
        // @ts-expect-error TS18048
        newY += deltaY / (zoomPerc * 0.45);
      };

      const updateLeft = () => {
        // @ts-expect-error TS18048
        newWidth -= deltaX / (zoomPerc * 0.45);
        // @ts-expect-error TS18048
        newX += deltaX / (zoomPerc * 0.45);
      };

      const updateRight = () => {
        // @ts-expect-error TS18048
        newWidth += deltaX / (zoomPerc * 0.45);
      };

      const updateBottom = () => {
        // @ts-expect-error TS18048
        newHeight += deltaY / (zoomPerc * 0.45);
      };

      if (changeType === StoryboardChangeType.RESIZE_TOP) {
        updateTop();
      } else if (changeType === StoryboardChangeType.RESIZE_LEFT) {
        updateLeft();
      } else if (changeType === StoryboardChangeType.RESIZE_BOTTOM) {
        updateBottom();
      } else if (changeType === StoryboardChangeType.RESIZE_RIGHT) {
        updateRight();
      } else if (changeType === StoryboardChangeType.RESIZE_BOTTOM_RIGHT) {
        updateBottom();
        updateRight();
      } else if (changeType === StoryboardChangeType.RESIZE_BOTTOM_LEFT) {
        updateBottom();
        updateLeft();
      } else if (changeType === StoryboardChangeType.RESIZE_TOP_LEFT) {
        updateTop();
        updateLeft();
      } else if (changeType === StoryboardChangeType.RESIZE_TOP_RIGHT) {
        updateTop();
        updateRight();
      } else if (changeType === StoryboardChangeType.MOVE) {
        // @ts-expect-error TS18048
        newX += deltaX / (zoomPerc * 0.45);
        // @ts-expect-error TS18048
        newY += deltaY / (zoomPerc * 0.45);
      }

      const minWidth =
        storyboard.type === STORYBOARD_TYPES.STORY
          ? MIN_STORY_WIDTH
          : MIN_STORYBOARD_WIDTH;
      const minHeight =
        storyboard.type === STORYBOARD_TYPES.STORY
          ? MIN_STORY_HEIGHT
          : MIN_STORYBOARD_HEIGHT;

      if (newWidth < minWidth) {
        newWidth = minWidth;
      } else if (newWidth > MAX_STORYBOARD_WIDTH) {
        newWidth = MAX_STORYBOARD_WIDTH;
      }

      if (newHeight < minHeight) {
        newHeight = minHeight;
      } else if (newHeight > MAX_STORYBOARD_HEIGHT) {
        newHeight = MAX_STORYBOARD_HEIGHT;
      }

      const newStoryboard = {
        ...storyboard,
        width: Math.round(newWidth),
        height: Math.round(newHeight),
        x: Math.round(newX),
        y: Math.round(newY),
        zIndex: maxZIndexRef.current
          ? maxZIndexRef.current + 10
          : storyboard.zIndex,
      };

      // Use local state for drag updates to avoid Redux re-renders
      setDraggedStoryboard(newStoryboard);
    } else if (drawingObjectRef.current || dragToSelectRef.current) {
      const x = drawingObjectRef.current
        ? // @ts-expect-error TS2339
          drawingObjectRef.current.x
        : // @ts-expect-error TS18047
          dragToSelectRef.current.x;
      const y = drawingObjectRef.current
        ? // @ts-expect-error TS2339
          drawingObjectRef.current.y
        : // @ts-expect-error TS18047
          dragToSelectRef.current.y;

      const { x: pointOnCanvasX, y: pointOnCanvasY } =
        getPointOnCanvasFromInsideIframe(
          e.clientX,
          e.clientY,
          canvasDivRef,
          iframesRef
        );

      const deltaX = pointOnCanvasX - x;
      const deltaY = pointOnCanvasY - y;

      let newWidth = deltaX;
      let newHeight = deltaY;

      if (
        drawingObjectRef.current &&
        // @ts-expect-error TS2339
        !drawingObjectRef.current.codebaseIdToDropInto
      ) {
        if (newWidth < -MAX_STORYBOARD_WIDTH) {
          newWidth = -MAX_STORYBOARD_WIDTH;
        } else if (newWidth > MAX_STORYBOARD_WIDTH) {
          newWidth = MAX_STORYBOARD_WIDTH;
        }

        if (newHeight < -MAX_STORYBOARD_HEIGHT) {
          newHeight = -MAX_STORYBOARD_HEIGHT;
        } else if (newHeight > MAX_STORYBOARD_HEIGHT) {
          newHeight = MAX_STORYBOARD_HEIGHT;
        }
      }

      if (drawingObjectRef.current) {
        const newStoryboard = {
          // @ts-expect-error TS2698
          ...drawingObjectRef.current,
          dragged: true,
          width: Math.round(newWidth),
          height: Math.round(newHeight),
        };

        setDrawingObject(newStoryboard);
      } else {
        const newDragToSelect = {
          // @ts-expect-error TS2698
          ...dragToSelectRef.current,
          width: newWidth,
          height: newHeight,
        };

        setDragToSelect(newDragToSelect);
      }
    }

    if (
      (cursorType === CursorType.Draw &&
        !drawingObjectRef.current &&
        // Application storyboards cannot be drawn inside another storyboard
        selectedLayoutOptionToUse.id !== LayoutId.APPLICATION) ||
      cursorType === CursorType.Text ||
      (changeType === StoryboardChangeType.MOVE &&
        changeStoryboardId &&
        // @ts-expect-error TS18047
        changeStoryboardRef.current.type !== STORYBOARD_TYPES.APPLICATION) ||
      isDraggingOutOfStoryboard
    ) {
      throttledCreateSyntheticOutlinesRef.current(e.clientX, e.clientY);
    } else {
      if (droppingStoryboard) {
        setDroppingStoryboard(false);
      }
    }
  };

  // Callbacks for components
  const setIframeRefCallback = useCallback(
    (storyboard, ref) =>
      (iframesRef.current[storyboard.id] = {
        ref,
        width: storyboard.width,
        height: storyboard.height,
      }),
    []
  );

  return (
    <>
      <DependencyConfirmationModalWrapper />
      <FileConflictModalWrapper />
      <CanvasSideEffect
        storyboardIdToSetHTML={storyboardIdToSetHTML}
        setStoryboardIdToSetHTML={setStoryboardIdToSetHTML}
      />
      {CopyPasteSideEffectInstance}
      {IFrameDragSideEffectInstance}
      {IframePostMessagingSideEffectInstance}

      <div
        className={
          "outline-none bg-secondary-200 grow p-0 overflow-hidden relative pointer-events-auto"
        }
        ref={canvasContainerRef}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onPointerMove={onCanvasMoveEvent}
        onMouseLeave={(e) => {
          updateMyPresence({
            cursor: null,
          });
        }}
      >
        <CanvasLoadingOverlay />
        <CanvasContextMenu
          showMenu={showContextMenu}
          left={menuLeft}
          top={menuTop}
          tempoElement={selectedElement}
          multiselectedElements={multiselectedElements}
        />
        <div id="styling-panel-portal-root" />
        <CanvasOverlay />
        <CanvasMovementOverlay
          canvasRef={canvasDivRef}
          iframesRef={iframesRef}
          // @ts-expect-error TS2322
          canvasContainerRef={canvasContainerRef}
        />
        <ScalablePanningLayer
          cursorStyle={cursorStyle}
          ref={canvasDivRef}
          onMouseDown={async (e) => {
            if (
              cursorType === CursorType.Draw ||
              cursorType === CursorType.Text
            ) {
              if (!drawingObjectRef.current) {
                await startDrawingObject(e);
              }
            } else if (cursorType !== CursorType.Drive) {
              startDragToSelect(e);

              dispatch(setSelectedElementKey(null));
            }
          }}
          onMouseUp={() => {
            endAllChanges();
            dispatch(setShowContextMenu(false));
            dispatch(setRequestedContextPosition(null));
          }}
          onMouseLeave={() => {
            endAllChanges();
          }}
        >
          <Annotations />
          {storyboardsToDraw.map((storyboard: any, index: number) => (
            <StoryboardView
              index={index}
              key={storyboard.id}
              storyboard={storyboard}
              forceShowOverlay={
                dragToSelect || draggingComponent || changeStoryboardId
              }
              allowHover={!changeStoryboardId && !isDragging && !drawingObject}
              iframeRef={setIframeRefCallback}
              startStoryboardChange={startStoryboardChange}
              startDrawingObject={startDrawingObject}
              endAllChanges={endAllChanges}
              onLoadIFrame={onLoadIFrame}
              canvasContainerRef={canvasContainerRef}
              // TO REMOVE -> these are meant to be abstracted away but not fully done during the iframe container refactor
              dragToSelect={dragToSelect}
              // @ts-expect-error TS2322
              changeStoryboardId={changeStoryboardId}
              droppingStoryboard={droppingStoryboard}
              changeType={changeType}
              hidden={storyboardsIdsToHide.has(storyboard.id)}
            />
          ))}

          {/* Iframe Pool - manages all iframes */}
          <IframePool
            storyboards={storyboardsToDraw}
            storyboardsIdsToHide={storyboardsIdsToHide}
            minZIndex={minZIndex}
            onLoadIFrame={onLoadIFrame}
            iframeRef={setIframeRefCallback}
            onMouseOut={() => {
              dispatch(setHoveredElementKey(null));
            }}
            onMouseUp={(e) => {
              endAllChanges();
              e.preventDefault();
              e.stopPropagation();
            }}
          />

          {/* Render loading storyboard with spinner */}
          {useCanvasStore.getState().loadingStoryboard ? (
            <LoadingSpinner
              loadingStoryboard={useCanvasStore.getState().loadingStoryboard!}
              maxZIndex={maxZIndex}
            />
          ) : null}

          {/*
           // @ts-expect-error TS2339 */}
          {drawingObject && !drawingObject.codebaseIdToAddTo && (
            <div
              className={`absolute flex flex-col select-none bg-white`}
              style={{
                position: "absolute",
                display: "flex",
                flexDirection: "column",

                // Middle of the canvas is 0, 0
                left:
                  CANVAS_WIDTH / 2 +
                  // @ts-expect-error TS2339
                  drawingObject.x +
                  // @ts-expect-error TS2339
                  (drawingObject.width < 0 ? drawingObject.width : 0),
                top:
                  CANVAS_HEIGHT / 2 +
                  // @ts-expect-error TS2339
                  drawingObject.y +
                  // @ts-expect-error TS2339
                  (drawingObject.height < 0 ? drawingObject.height : 0),
                transformOrigin: "top left",

                // @ts-expect-error TS2339
                zIndex: drawingObject.zIndex - minZIndex,

                // @ts-expect-error TS2339
                width: Math.abs(drawingObject.width),
                // @ts-expect-error TS2339
                height: Math.abs(drawingObject.height),
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            ></div>
          )}

          {canvasComponentKey &&
            (() => {
              const storyStoryboards = storyboardsToDraw.filter(
                (sb) =>
                  sb.type === STORYBOARD_TYPES.STORY &&
                  !sb.deleted &&
                  !sb.preCreated
              );
              const selectedStoryboard = storyboardsToDraw.find((sb) =>
                selectedElement.isStoryboard(sb.id)
              );
              const selectedStory =
                selectedStoryboard?.type === STORYBOARD_TYPES.STORY &&
                (multiselectedElements?.length || 0) <= 1;

              if (storyStoryboards.length !== 0 && !selectedStory) {
                return;
              }

              const lastStoryStoryboard =
                storyStoryboards[storyStoryboards.length - 1];

              let x, y, width, height;
              if (selectedStoryboard && lastStoryStoryboard) {
                x = lastStoryStoryboard.x + lastStoryStoryboard.width + 100;
                y = lastStoryStoryboard.y;
                width = selectedStoryboard.width;
                height = selectedStoryboard.height;
              } else {
                x = -100;
                y = -50;
                width = 200;
                height = 100;
              }

              return (
                <div
                  className={`absolute flex flex-row select-none border border-secondary-500 rounded-lg items-center justify-center border-dashed p-2 text-sm cursor-pointer bg-secondary-300 hover:bg-secondary-400 text-secondary-900 hover:text-white`}
                  style={{
                    position: "absolute",
                    display: "flex",
                    flexDirection: "column",

                    // Middle of the canvas is 0, 0
                    left: CANVAS_WIDTH / 2 + x,
                    top: CANVAS_HEIGHT / 2 + y,
                    transformOrigin: "top left",

                    zIndex: maxZIndex + 20,

                    width: width,
                    height: height,
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    // If copying another story, use its codebase ID
                    const createSourceCodebaseId =
                      selectedStoryboard?.savedCodebaseId ||
                      canvasComponent?.codebaseId;
                    const createName = selectedStoryboard
                      ? selectedStoryboard.name
                      : "Default";

                    createNewVariant(
                      x,
                      y,
                      width,
                      height,
                      // @ts-expect-error TS2345
                      createName,
                      createSourceCodebaseId
                    );
                  }}
                >
                  <div className="flex flex-row justify-center items-center gap-2">
                    <PlusCircledIcon className="w-4 h-4" /> Variant
                  </div>
                </div>
              );
            })()}

          {dragToSelect && (
            <div
              className={`bg-blue-500 opacity-30`}
              style={{
                position: "absolute",

                // Middle of the canvas is 0, 0
                left:
                  CANVAS_WIDTH / 2 +
                  // @ts-expect-error TS2339
                  dragToSelect.x +
                  // @ts-expect-error TS2339
                  (dragToSelect.width < 0 ? dragToSelect.width : 0),
                top:
                  CANVAS_HEIGHT / 2 +
                  // @ts-expect-error TS2339
                  dragToSelect.y +
                  // @ts-expect-error TS2339
                  (dragToSelect.height < 0 ? dragToSelect.height : 0),
                transformOrigin: "top left",

                zIndex: maxZIndex + 1000,

                // @ts-expect-error TS2339
                width: Math.abs(dragToSelect.width),
                // @ts-expect-error TS2339
                height: Math.abs(dragToSelect.height),
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          )}
          {DragOutOutlineInstance}
          <ClientSideSuspense fallback={<></>}>
            <LiveblocksCursors
              canvasHeight={CANVAS_HEIGHT}
              canvasWidth={CANVAS_WIDTH}
            />
          </ClientSideSuspense>
        </ScalablePanningLayer>
      </div>
    </>
  );
});

Canvas.displayName = "Canvas";

export default Canvas;
